import { Link } from "react-router-dom";
import { Sparkles, Wand2, ArrowRight } from "lucide-react";
import { PageHeader } from "./PageHeader";
import { cn } from "../../lib/utils";

/**
 * Reusable Lab landing page.
 * Props:
 *  - kicker, title, description
 *  - heroImage (url), heroTone ("aqua" | "lime" | "mixed")
 *  - features: [{icon, title, description}]
 *  - actions: [{label, to, variant}]
 *  - styles: [{title, description, tag}] — premium style modes
 *  - sampleAssets: [{title, thumb, meta}] — recent items
 *  - quickActions: [{label, icon, onClick?}]
 */
export default function LabPage({
  kicker,
  title,
  description,
  heroImage,
  heroTone = "mixed",
  features = [],
  actions = [],
  styles = [],
  sampleAssets = [],
  quickActions = [],
  testId,
}) {
  return (
    <div data-testid={testId || "lab-page"} className="space-y-10">
      <PageHeader
        kicker={kicker}
        title={title}
        description={description}
        actions={
          actions.length ? (
            <>
              {actions.map((a, i) => (
                <Link
                  key={i}
                  to={a.to || "/create"}
                  data-testid={`lab-action-${i}`}
                  className={cn(
                    "inline-flex items-center gap-2 px-4 h-11 rounded-xl text-sm",
                    a.variant === "ghost"
                      ? "border border-[var(--pl-border)] text-[var(--pl-text)] hover:border-[var(--pl-aqua)]/40"
                      : a.variant === "lime"
                      ? "pl-btn-lime"
                      : "pl-btn-aqua"
                  )}
                >
                  {a.icon ? <a.icon className="h-4 w-4" /> : <Wand2 className="h-4 w-4" />}
                  {a.label}
                </Link>
              ))}
            </>
          ) : (
            <Link
              to="/create"
              className="inline-flex items-center gap-2 px-4 h-11 rounded-xl pl-btn-aqua text-sm"
            >
              <Wand2 className="h-4 w-4" /> Create with AI
            </Link>
          )
        }
      />

      {/* Hero block */}
      <div
        className="relative overflow-hidden rounded-3xl border border-[var(--pl-border)] p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-5 gap-6"
        style={{
          background:
            heroTone === "lime"
              ? "linear-gradient(135deg, rgba(185,255,47,0.10), rgba(11,47,44,0.7))"
              : heroTone === "aqua"
              ? "linear-gradient(135deg, rgba(39,230,209,0.10), rgba(11,47,44,0.7))"
              : "linear-gradient(135deg, rgba(39,230,209,0.08), rgba(185,255,47,0.06), rgba(11,47,44,0.6))",
        }}
      >
        <div className="lg:col-span-3 space-y-4 relative">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">
            <Sparkles className="h-3.5 w-3.5" /> Premier-grade
          </div>
          <p className="text-sm text-[var(--pl-text)]/80 max-w-xl">
            {description}
          </p>
          {quickActions.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2">
              {quickActions.map((q, i) => {
                const I = q.icon || Sparkles;
                return (
                  <button
                    key={i}
                    data-testid={`lab-qa-${i}`}
                    className="inline-flex items-center gap-2 px-3.5 h-10 rounded-xl border border-[var(--pl-border)] bg-white/[0.03] text-sm text-[var(--pl-text)] hover:border-[var(--pl-aqua)]/40 transition"
                  >
                    <I className="h-4 w-4 text-[var(--pl-aqua)]" /> {q.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="lg:col-span-2 relative aspect-[4/3] rounded-2xl overflow-hidden border border-[var(--pl-border)]">
          {heroImage ? (
            <img src={heroImage} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full pl-signature-gradient" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        </div>
      </div>

      {/* Features grid */}
      {features.length > 0 && (
        <section className="space-y-3">
          <Eyebrow>Capabilities</Eyebrow>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((f, i) => {
              const I = f.icon || Sparkles;
              return (
                <div
                  key={i}
                  data-testid={`lab-feat-${i}`}
                  className="pl-fade-up rounded-2xl p-5 border border-[var(--pl-border)] bg-[var(--pl-surface)]/60 backdrop-blur-md pl-lift pl-contour-card"
                  style={{ animationDelay: `${i * 40}ms` }}
                >
                  <div className="h-10 w-10 rounded-xl bg-[var(--pl-aqua)]/10 border border-[var(--pl-aqua)]/30 grid place-items-center mb-3">
                    <I className="h-5 w-5 text-[var(--pl-aqua)]" strokeWidth={1.7} />
                  </div>
                  <div className="font-medium text-[var(--pl-text)]">{f.title}</div>
                  <div className="text-xs text-[var(--pl-text-muted)] mt-1.5">{f.description}</div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Style presets */}
      {styles.length > 0 && (
        <section className="space-y-3">
          <Eyebrow>Premium Styles</Eyebrow>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {styles.map((s, i) => (
              <div
                key={i}
                data-testid={`lab-style-${i}`}
                className="rounded-2xl p-5 border border-[var(--pl-border)] bg-[var(--pl-surface)]/60 pl-lift cursor-pointer relative overflow-hidden"
              >
                <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-[var(--pl-aqua)]/15 blur-3xl pointer-events-none" />
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-lime)]">
                  {s.tag || "Style"}
                </div>
                <div className="font-display text-base font-medium text-[var(--pl-text)] mt-1">{s.title}</div>
                <div className="text-xs text-[var(--pl-text-muted)] mt-1.5">{s.description}</div>
                <div className="mt-4 inline-flex items-center gap-1 text-xs text-[var(--pl-aqua)]">
                  Use style <ArrowRight className="h-3 w-3" />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Sample assets */}
      {sampleAssets.length > 0 && (
        <section className="space-y-3">
          <Eyebrow>Recent in this Lab</Eyebrow>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {sampleAssets.map((a, i) => (
              <div
                key={i}
                data-testid={`lab-asset-${i}`}
                className="rounded-2xl overflow-hidden border border-[var(--pl-border)] bg-[var(--pl-surface)]/70 pl-lift"
              >
                <div className="aspect-[4/5] bg-[var(--pl-bg-2)] overflow-hidden">
                  <img src={a.thumb} alt={a.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-3">
                  <div className="text-sm font-medium text-[var(--pl-text)] truncate">{a.title}</div>
                  {a.meta && <div className="text-[11px] text-[var(--pl-text-muted)]">{a.meta}</div>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function Eyebrow({ children }) {
  return (
    <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">{children}</div>
  );
}
